/**
 * @file 反馈卡片内容
 * @author malcolmmzma
 */

import React, { forwardRef, useImperativeHandle } from 'react';
import { MetaInput, Text } from '@tencent/odc';
import { ButtonGroup } from '@tencent/spaui';
import { Controller, useForm } from 'react-hook-form';

import EmojiList from './EmojiList';
import { DISCONTENT_DATA } from './constants';

interface FeedbackContentForm {
  score: number; // 满意度
  discontent?: string[]; // 不满意的方面
  suggestion?: string; // 建议
}

export interface FeedbackContentProps extends React.HTMLProps<HTMLDivElement> {
  productName?: string; // 产品名称
}

export interface FeedbackContentRef {
  onSubmit: (submitAction: (data: FeedbackContentForm) => Promise<void>) => Promise<void>; // 提交
}

const FeedbackContent = forwardRef<FeedbackContentRef, FeedbackContentProps>((props, ref) => {
  const { productName = '产品', className, ...restProps } = props;

  const { handleSubmit, control, watch } = useForm<FeedbackContentForm>();

  const onSubmit: FeedbackContentRef['onSubmit'] = (submitAction) => handleSubmit(async (data) => {
    await submitAction(data);
  })();
  const watchedScore = watch('score');

  useImperativeHandle(ref, () => ({
    onSubmit,
  }));

  return (
    <div className={`ai-agent-feedback-card l-metas l-gen-metas ${className}`} {...restProps}>
      <div className="ai-agent-feedback-card-item ai-agent-feedback-card__rate">
        <label>你对 {productName} 是否满意？</label>
        <Controller
          name="score"
          rules={{ required: '请选择满意度' }}
          control={control as any}
          render={({ field, fieldState }) => (
            <EmojiList
              {...field}
              value={field.value}
              onChange={(val) => field.onChange(val)}
              feedback={fieldState.error ? { status: 'error', message: fieldState.error.message } : undefined}
            />
          )}
        />
      </div>
      {watchedScore <= 3 && (
        <div className="ai-agent-feedback-card-item ai-agent-feedback-card__discontent">
          <label>你认为哪些方面不满意？</label>
          <Controller
            name="discontent"
            control={control as any}
            rules={{ required: '请选择不满意的方面' }}
            render={({ field, fieldState }) => (
              <div>
                <ButtonGroup
                  className="l-segmented light"
                  ButtonProps={{
                    hasSpan: true,
                  }}
                  multiple
                  data={DISCONTENT_DATA}
                  value={field.value}
                  onChange={(_e, val) => {
                    field.onChange(val);
                  }}
                />
                {fieldState.error && (
                  <Text size="small" type="danger">
                    {fieldState.error.message}
                  </Text>
                )}
              </div>
            )}
          />
        </div>
      )}
      <div className="ai-agent-feedback-card-item ai-agent-feedback-card__suggestion">
        <label>
          你对 {productName} 还有哪些建议？<span className="tw-text-text-disabled">（选填）</span>
        </label>
        <Controller
          name="suggestion"
          control={control as any}
          render={({ field }) => (
            <MetaInput
              className="l-input"
              placeholder="请输入你的意见或建议"
              useTextarea
              style={{ border: 'none' }}
              value={field.value}
              onChange={(_e, val) => {
                field.onChange(val);
              }}
            />
          )}
        />
      </div>
    </div>
  );
});

FeedbackContent.displayName = 'FeedbackContent';

export default FeedbackContent;
